/**
 * Bearer 토큰 저장소.
 *
 * Flutter bridge `getAuthToken` 응답(accessToken)을 메모리에 보관 → axios 인터셉터에서 꺼내
 * `Authorization: Bearer ${token}` 헤더로 자동 주입.
 *
 * 비회원은 token이 없는 상태로 진행 — 광역권역 조회는 시스템 인증(x-api-key)으로 동작,
 * 사용자 API(waitlist, order/availability)는 401로 실패 (애초에 비회원이 도달 X).
 *
 * 외부 store 패턴 (subscribe + getSnapshot) — useSyncExternalStore 와 결합해 컴포넌트가
 * token 변경(예: 회원가입 후 setToken)을 즉시 인지하고 re-render. 사용처: hooks/useHasToken.
 *
 * 토큰 갱신 경로:
 *  - `useInitAuth` 가 App 마운트 / visibility 복귀 시 `registerTokenFetcher` 로 등록한 fetcher 실행
 *  - axios 401 interceptor 가 `refreshTokenFromBridge` 호출해 자동 재시도
 */

let token: string | null = null
const listeners = new Set<() => void>()

/** 외부(예: useInitAuth)가 등록하는 토큰 fetcher. 보통 Flutter 브릿지 callHandler 래퍼 */
type TokenFetcher = () => Promise<string | null>
let tokenFetcher: TokenFetcher | null = null

export const setToken = (value: string): void => {
  token = value
  listeners.forEach((listener) => listener())
}

export const getToken = (): string | null => token

/** 토큰 보유 여부 — 함수 호출 시점 기준. React 컴포넌트는 useHasToken hook 사용 */
export const hasToken = (): boolean => token !== null && token !== ''

/**
 * 외부 store subscription — useSyncExternalStore 의 첫 인자.
 *
 * setToken 호출 시 등록된 listener 가 모두 호출되어 React 가 snapshot 을 다시 읽고 re-render.
 */
export const subscribeToken = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * 현재 token snapshot — useSyncExternalStore 의 두번째 인자.
 *
 * primitive(string|null) 라 reference equality 안전 (매 호출마다 같은 값이면 동일 참조).
 */
export const getTokenSnapshot = (): string | null => token

/**
 * 토큰 fetcher 등록. null 전달 시 해제 (테스트/cleanup 용).
 *
 * 일반적으로 `useInitAuth` 가 브릿지 callHandler('getAuthToken') 래퍼를 등록.
 * 외부(axios interceptor, visibilitychange 핸들러)가 React 컨텍스트 없이 토큰 재요청 가능하게 해줌.
 */
export const registerTokenFetcher = (fetcher: TokenFetcher | null): void => {
  tokenFetcher = fetcher
}

/**
 * 등록된 fetcher 로 토큰을 재요청. 성공 시 setToken 후 true 반환.
 *
 *  - fetcher 미등록 → false (토큰 미변경)
 *  - fetcher 반환값 null/'' → false (토큰 미변경)
 *  - fetcher throw → false (토큰 미변경)
 *  - 정상 토큰 반환 → setToken + true
 */
export const refreshTokenFromBridge = async (): Promise<boolean> => {
  if (!tokenFetcher) return false
  try {
    const newToken = await tokenFetcher()
    if (newToken == null || newToken === '') return false
    setToken(newToken)
    return true
  } catch {
    return false
  }
}
